import React, { useEffect, useState } from 'react';
import { RefreshCw, HardDrive, AlertTriangle, Info, FileWarning } from 'lucide-react';
import { storageApi } from '../../lib/api';

function fmtBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function AdminStorage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try { setReport(await storageApi.report()); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load storage report'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0b1f3a]">Storage</h2>
          <p className="text-slate-500 text-sm mt-1">
            Emergent-managed object storage bucket (shared with EasySpanishTax). Audit usage, find orphaned files.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn-outline disabled:opacity-60" data-testid="admin-storage-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {err && (
        <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2 text-sm">{err}</div>
      )}

      {!report && !err && (
        <p className="text-slate-500 text-sm">Loading storage report…</p>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <StatCard icon={HardDrive} label="Total objects" value={report.total_objects.toLocaleString()} />
            <StatCard icon={HardDrive} label="Total size"    value={fmtBytes(report.total_bytes)} />
            <StatCard icon={FileWarning} label="Orphan files" value={report.orphan_count.toLocaleString()} tone="amber" />
            <StatCard icon={FileWarning} label="Orphan size" value={fmtBytes(report.orphan_bytes)} tone="amber" />
          </div>

          <div className="mb-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-start gap-3">
            <Info size={18} className="text-slate-500 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-600 leading-relaxed">
              {report.note}
            </p>
          </div>

          {report.missing_count > 0 && (
            <div className="mb-5 bg-rose-50 border border-rose-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-rose-600" />
                <h3 className="font-semibold text-[#0b1f3a] text-sm">Missing files ({report.missing_count})</h3>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                These paths are referenced by the database but no longer exist in the bucket. The corresponding downloads will fail.
              </p>
              <ul className="text-xs font-mono text-slate-700 space-y-1 max-h-64 overflow-y-auto">
                {report.missing.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[#0b1f3a] text-sm">Orphaned objects</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Files still in the bucket that no DB record references. Largest first.
                </p>
              </div>
              <span className="text-xs text-slate-500">{report.orphan_count} total</span>
            </div>
            {report.orphans.length === 0 ? (
              <p className="p-10 text-center text-slate-500 text-sm">🎉 No orphans found — every stored file is referenced.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Path</th>
                    <th className="text-right px-4 py-2 font-semibold">Size</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.orphans.map((o) => (
                    <tr key={o.path} className="hover:bg-slate-50/50">
                      <td className="px-4 py-2 font-mono text-xs text-slate-700 truncate max-w-[520px]">{o.path}</td>
                      <td className="px-4 py-2 text-right text-slate-600 whitespace-nowrap">{fmtBytes(o.size)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const toneCls =
    tone === 'amber'
      ? 'bg-amber-50 border-amber-200 text-amber-700'
      : 'bg-white border-slate-200 text-[#0d3a8a]';
  return (
    <div className={`border rounded-2xl p-4 ${toneCls}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold opacity-80">
        <Icon size={14} /> {label}
      </div>
      <p className="mt-2 text-2xl font-bold text-[#0b1f3a]">{value}</p>
    </div>
  );
}
